import app from '@src/helpers/app';
import {IHttp} from '@src/@interfaces/services/Http/IHttp';
import NotFoundException from '@src/exceptions/NotFoundException';
import ApiRoute from '@src/services/Router/ApiRoute';
import LogicException from '@src/exceptions/LogicException';
import {HttpEvents as Events, RequestOptions} from '@src/@types/services/Http/Http';
import {IEvent, IListener, ISubscription} from '@src/@interfaces/services/Events';
import UnexpectedResponseHttpException from '@src/exceptions/UnexpectedResponseHttpException';
import AbstractBaseError from '@src/exceptions/AbstractBaseError';
import {EventPayload} from '@src/@types/services/Events/EventPayload';
import trans from '@src/helpers/trans';
import {HTTPMethods} from '@src/@interfaces/http/HTTPMethods';
import {isPlainObject} from '@src/helpers/is-plain-object';
import {convertObjectToFormData} from '@src/helpers/formDataConverter';
import URLSearchParamsGenerator from '@src/helpers/URLSearchParamsGenerator';
import HttpExceptionFactory from './HttpExceptionFactory';
import {IHttpExceptionFactory} from '@src/@interfaces/services/Http/IHttpExceptionFactory';

export default class Http implements IHttp {
    protected defaultOptions: RequestOptions = {
        method: 'get',
        mode: 'cors',
        credentials: 'same-origin'
    };

    protected exceptionFactoryInstance?: IHttpExceptionFactory;

    protected requestsSender: typeof fetch | null = null;

    protected shouldReturnRawResponse = false;

    request(url: string, options?: RequestOptions): Promise<any> {
        return this.sendRequest(url, options);
    }

    route<TResponse = any>(
        routeName: string,
        routeUrlParams?: string[] | Record<string, string>,
        options?: RequestOptions,
    ): Promise<TResponse> {
        const route = app('url').getRouteByName(routeName);
        if (!route) {
            return Promise.reject(
                new NotFoundException(
                    trans('Exceptions.route_with_name_not_found', {
                        name: routeName
                    })
                )
            );
        }

        if (!(route instanceof ApiRoute)) {
            return Promise.reject(
                new LogicException(
                    trans('Exceptions.services.http.route_with_name_must_be_instance_of_api_route', {
                        name: routeName
                    })
                )
            );
        }

        const requestMethod = route.getMethods().at(0);
        const url = route.resolveUrl(routeUrlParams);

        return this.sendRequest(url, Object.assign({method: requestMethod}, options ?? {}));
    }

    listen<TPayload>(event: Events | Events[], listener: IListener<TPayload>): ISubscription {
        return this.events.listen(event, listener);
    }

    withRequestsSender<T>(sender: typeof fetch, callable: (http: IHttp) => T): T {
        const originalSender = this.requestsSender;
        this.requestsSender = sender;

        try {
            return callable(this);
        } finally {
            this.requestsSender = originalSender;
        }
    }

    withRawResponse<T>(callable: (http: IHttp) => T): T {
        const originalState = this.shouldReturnRawResponse;
        this.shouldReturnRawResponse = true;

        try {
            return callable(this);
        } finally {
            this.shouldReturnRawResponse = originalState;
        }
    }

    protected sendRequest(url: string, options?: RequestOptions) {
        const requestOptions = this.mergeOptions(options);
        requestOptions.headers = new Headers(requestOptions.headers);

        if (String(requestOptions.method ?? 'get').toUpperCase() === HTTPMethods.GET && requestOptions.body) {
            let searchParams = null;
            if (typeof requestOptions.body === 'string') {
                searchParams = requestOptions.body;
            } else if (isPlainObject(requestOptions.body)) {
                searchParams = URLSearchParamsGenerator.toString(requestOptions.body);
            }

            if (searchParams) {
                url += (url.includes('?') ? '&' : '?') + searchParams;
            }

            delete requestOptions.body;
        }

        if (
            // fix Symfony bug, https://github.com/laravel/framework/issues/13457
            requestOptions.body && (
                requestOptions.headers.get('Content-Type') === 'multipart/form-data' ||
                requestOptions.body instanceof FormData
            ) ||
            // fix bug. Nginx can't handle patch request, return 400 bad request
            requestOptions.method === 'patch'
        ) {
            requestOptions.headers.delete('Content-Type');

            if (isPlainObject(requestOptions.body)) {
                requestOptions.body = convertObjectToFormData(requestOptions.body);
            }

            if (requestOptions.body instanceof FormData) {
                requestOptions.body.append('_method', requestOptions.method ?? 'get');
            }

            requestOptions.method = 'post';
        }

        if (
            requestOptions.headers.get('Content-Type') === 'application/json' &&
            (isPlainObject(requestOptions.body) || Array.isArray(requestOptions.body))
        ) {
            requestOptions.body = JSON.stringify(requestOptions.body);
        }

        this.dispatchEvent(Events.BEFORE_SEND_REQUEST, {url, options: requestOptions});

        const shouldReturnRawResponse = this.shouldReturnRawResponse;

        // laravel php debug bar proxied window.fetch function, so we must use this one
        const sender = this.requestsSender ?? window.fetch;

        return sender(url, requestOptions as RequestInit).then(async (res) => {
            this.dispatchEvent(Events.REQUEST_COMPLETED, {url, options: requestOptions, response: res});

            return shouldReturnRawResponse ? res : this.handleHttpResponse(res);
        }).then(
            response => {
                this.dispatchEvent(Events.REQUEST_SUCCESS, {url, options: requestOptions, response});

                return response;
            },
            error => {
                this.dispatchEvent(Events.REQUEST_FAIL, {url, options: requestOptions, error});

                throw error;
            }
        );
    }

    protected mergeOptions(options?: RequestOptions): RequestOptions {
        if (options?.json) {
            options.headers ??= {};
            if (Array.isArray(options.headers)) {
                if (options.headers.every(pair => pair[0].toLowerCase() !== 'content-type')) {
                    options.headers.push(['content-type', 'application/json']);
                }
            } else if (options.headers instanceof Headers) {
                if (!options.headers.has('content-type')) {
                    options.headers.set('content-type', 'application/json');
                }
            } else if (Object.keys(options.headers).every(pair => pair[0].toLowerCase() !== 'content-type')) {
                options.headers['content-type'] = 'application/json';
            }

            options.body ??= {};
            if (options.body instanceof URLSearchParams) {
                Object.keys(options.json).forEach(key => {
                    (options.body as URLSearchParams).set(key, String((options.json as any)[key]));
                });
            } else if (options.body instanceof FormData) {
                Object.keys(options.json).forEach(key => {
                    (options.body as FormData).set(key, (options.json as any)[key]);
                });
            } else if (isPlainObject(options.body)) {
                options.body = {...options.body, ...options.json};
            }
        }

        return Object.assign(
            {},
            this.getDefaultOptions(),
            options || {},
            {
                headers: Object.assign({}, {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': app('CSRFManager').CSRFToken,
                    'X-XSRF-TOKEN': app('CSRFManager').XSRFToken,
                }, options?.headers || {})
            },
        );
    }

    protected getDefaultOptions(): RequestOptions {
        return this.defaultOptions;
    }

    protected async handleHttpResponse(res: Response) {
        return res.text().then(text => {
            let invalidResponse = false;
            const data = (() => {
                try {
                    return res.status === 204 ? null : JSON.parse(text);
                } catch (e) {
                    invalidResponse = true;

                    return null;
                }
            })();

            if (!res.ok) {
                throw this.resolveExceptionInstanceByResponse(res, text, data);
            }

            if (invalidResponse) {
                throw new UnexpectedResponseHttpException(res);
            }

            return data;
        }).catch(error => {
            if (error instanceof AbstractBaseError) {
                throw error;
            }

            throw this.resolveExceptionInstanceByResponse(res, undefined, undefined, error);
        });
    }

    protected resolveExceptionInstanceByResponse(response: Response, textResponse?: string, data?: any, error?: Error): Error {
        return this.getExceptionFactory().createException(response, textResponse, data, error);
    }

    protected get events() {
        return app('events');
    }

    protected dispatchEvent(event: IEvent | string, payload?: EventPayload, halt?: boolean): any | any[] | null {
        this.events.dispatch(event, payload, halt);
    }

    protected getExceptionFactory(): IHttpExceptionFactory {
        return this.exceptionFactoryInstance ??= new HttpExceptionFactory();
    }
}
